/*
 * Copyright (c) Domotz Ltd. All rights reserved.
 */

import React from 'react';
import {Link} from 'react-router-dom';
import intl from 'react-intl-universal';

import {scoreDecimals, toCompactAmount} from "../../helpers/IspScoreHelper";

import ScoreIndicator from "../../components/ScoreIndicator";

class IspSidebarSummary extends React.Component {

    render() {
        const name = `${this.props.isp.name || ''}`;
        const logoUrl = this.props.isp.logoImageUrl;

        return (
            <div>
                {this.renderLastOutage()}
                <div className="mb-3 mx-auto">
                    <img className="img-fluid rounded-square" src={logoUrl} width="110"
                         height="110" alt={name + " logo"}/>
                </div>

                <h3 className="h5 mb-0">{name}</h3>
                <div className="mb-4">
                    {this.renderWeblink()}
                </div>

                <div className="mb-4">
                    {this.renderPerformanceScore()}
                </div>

                <div className="mb-4">
                    {this.renderSentimentScore()}
                </div>

                <div className="mb-4">
                    {this.renderContactSupport()}
                </div>
            </div>
        );
    }

    renderLastOutage() {

        const outage = this.props.lastOutage;
        if (outage) {
            if (outage.active) {
                return (
                        <Link to={'/outage/' + outage.reportId}>
                            <div className="card card-frame border-1 mb-3 text-white bg-danger h5">
                               {intl.getHTML('isp_sidebar_outage_ongoing')}
                            </div>
                        </Link>
                        );
            } else if (outage.recent) {
                return (
                        <Link to={'/outage/' + outage.reportId}>
                            <div className="card card-frame border-1 mb-3 text-dark bg-warning h5">
                               {intl.getHTML('isp_sidebar_outage_recent')}
                            </div>
                        </Link>
                        );
            }
        }

        return '';
    }

    renderWeblink() {
        if (this.props.isp.websiteUrl) {
            let cleanName = this.props.isp.websiteUrl.replace('https://', '').replace('http://', '');
            return (
                <a className="d-block font-size-1" target="_blank"
                   rel="noopener noreferrer"
                   href={this.props.isp.websiteUrl}>{cleanName}</a>
            );
        } else {
            return '';
        }
    }

    renderContactSupport() {
        if (this.props.isp.supportUrl ||
            this.props.isp.supportPhone ||
            this.props.isp.supportFacebookUrl ||
            this.props.isp.supportTwitterUrl) {
            return (
                <div className="mt-6">
                    <h4 className="font-size-1 font-weight-normal text-secondary">{intl.get('isp_sidebar_support')}</h4>

                    <ul className="list-inline mb-0">
                        {
                            this.props.isp.supportFacebookUrl &&
                            <li className="list-inline-item mb-3">
                                <a className="btn btn-sm btn-icon btn-soft-primary rounded-circle"
                                   href={this.props.isp.supportFacebookUrl}
                                   rel="noopener noreferrer"
                                   target="_blank"
                                >
                                    <span className="fab fa-facebook-f btn-icon__inner"/>
                                </a>
                            </li>
                        }
                        {
                            this.props.isp.supportTwitterUrl &&
                            <li className="list-inline-item mb-3">
                                <a className="btn btn-sm btn-icon btn-soft-primary rounded-circle"
                                   href={this.props.isp.supportTwitterUrl}
                                   rel="noopener noreferrer"
                                   target="_blank"
                                >
                                    <svg 
                                        className="x-twitter"

                                        xmlns="http://www.w3.org/2000/svg" 
                                        height="16px" 
                                        width="16px"
                                        viewBox="0 0 512 512">
                                        <path fill="#8c98a4" d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z"/>
                                    </svg>
                                </a>
                            </li>
                        }
                        {
                            this.props.isp.supportUrl &&
                            <li className="list-inline-item mb-3">
                                <a className="btn btn-sm btn-icon btn-soft-primary rounded-circle"
                                   href={this.props.isp.supportUrl}
                                   rel="noopener noreferrer"
                                   target="_blank"
                                >
                                    <span className="fa fa-globe btn-icon__inner"/>
                                </a>
                            </li>
                        }
                        {
                            this.props.isp.supportPhone &&
                            <li className="list-inline-item mb-3">
                                <a className="btn btn-sm btn-icon btn-soft-primary rounded-circle" href={this.props.isp.supportPhone}>
                                    <span className="fa fa-phone btn-icon__inner"/>
                                </a>
                            </li>
                        }
                    </ul>
                </div>
            )
        } else {
            return '';
        }
    }

    renderPerformanceScore() {
        const score = scoreDecimals(this.props.isp.ispScore.scoreValue / (100.0 / 5), 1);
        const samples = toCompactAmount(this.props.isp.ispScore.samples);
        if (score === 0 || samples === 0)
            return '';

        return (
            <div>
                <ScoreIndicator score={this.props.isp.ispScore.scoreValue}/>
                <span className="font-weight-semi-bold ml-2">{score}</span>
                <div>
                        <span className="small text-secondary mr-2">
                            {intl.get('isp_sidebar_tested', {samples: samples})}
                        </span>
                </div>
            </div>
        );
    }

    renderSentimentScore() {
        let sentiment = scoreDecimals(this.props.isp.ispSentiment.sentimentValue / (100.0 / 5), 1);
        const ratings = this.props.isp.ispSentiment.totalRatings;
        if (sentiment === 0 || ratings === 0)
            return '';

        return (
            <div>
                <ScoreIndicator score={this.props.isp.ispSentiment.sentimentValue}
                                fullClass="fas fa-heart"
                                halfClass="fas fa-heart"
                                emptyClass="far fa-heart" />
                <span className="font-weight-semi-bold ml-2">{sentiment}</span>
                <div>
                        <span className="small text-secondary mr-2">
                            {intl.get('isp_sidebar_rated', {ratings: ratings})}
                        </span>
                </div>
            </div>
        );
    }
}

export default IspSidebarSummary;